import {
  UTModal,
  UTBox,
  UTTypography,
  UTLoading,
  ColorVariables,
} from '@group-link-one/gl-ui/utilities';
import type { UserProfile } from '../../Types/UserManagement.types';

interface UserDetailsModalProps {
  open: boolean;
  onClose: () => void;
  profile: UserProfile | null;
  isLoading?: boolean;
}

export const UserDetailsModal = ({
  open,
  onClose,
  profile,
  isLoading = false,
}: UserDetailsModalProps) => {
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const isLocked = profile?.lockedUntil && new Date(profile.lockedUntil) > new Date();

  return (
    <UTModal
      title={profile ? `User: ${profile.name}` : 'User Details'}
      open={open}
      onClose={onClose}
      hasButtonClose
      maxWidth='600px'
      content={
        isLoading ? (
          <UTBox display='flex' justifyContent='center' alignItems='center' padding='40px'>
            <UTLoading type='dots' />
          </UTBox>
        ) : profile ? (
          <UTBox flexDirection='column' gap='20px' py='20px'>
            {/* Profile Information */}
            <UTBox flexDirection='column' gap='12px'>
              <UTTypography fontSize='13px' fontWeight='600' color='rgba(0,0,0,0.7)'>
                Profile Information
              </UTTypography>

              <UTBox
                flexDirection='row'
                justifyContent='space-between'
                py='8px'
                borderBottom='1px solid rgba(0,0,0,0.1)'
              >
                <UTTypography fontSize='13px' color='rgba(0,0,0,0.7)'>
                  Name:
                </UTTypography>
                <UTTypography fontSize='13px' fontWeight='600'>
                  {profile.name}
                </UTTypography>
              </UTBox>

              <UTBox
                flexDirection='row'
                justifyContent='space-between'
                py='8px'
                borderBottom='1px solid rgba(0,0,0,0.1)'
              >
                <UTTypography fontSize='13px' color='rgba(0,0,0,0.7)'>
                  Email:
                </UTTypography>
                <UTTypography fontSize='13px' fontWeight='600'>
                  {profile.email}
                </UTTypography>
              </UTBox>

              <UTBox
                flexDirection='row'
                justifyContent='space-between'
                py='8px'
                borderBottom='1px solid rgba(0,0,0,0.1)'
              >
                <UTTypography fontSize='13px' color='rgba(0,0,0,0.7)'>
                  Created:
                </UTTypography>
                <UTTypography fontSize='13px' fontWeight='600'>
                  {formatDate(profile.createdAt)}
                </UTTypography>
              </UTBox>

              <UTBox flexDirection='row' justifyContent='space-between' py='8px'>
                <UTTypography fontSize='13px' color='rgba(0,0,0,0.7)'>
                  Updated:
                </UTTypography>
                <UTTypography fontSize='13px' fontWeight='600'>
                  {formatDate(profile.updatedAt)}
                </UTTypography>
              </UTBox>
            </UTBox>

            {/* Permissions */}
            {profile.permissions && profile.permissions.length > 0 && (
              <UTBox flexDirection='column' gap='12px'>
                <UTTypography fontSize='13px' fontWeight='600' color='rgba(0,0,0,0.7)'>
                  Direct Permissions ({profile.permissions.length})
                </UTTypography>
                <UTBox
                  flexDirection='column'
                  gap='6px'
                  pl='12px'
                  maxHeight='200px'
                  overflowY='auto'
                >
                  {profile.permissions.map((perm, idx) => (
                    <UTTypography key={idx} fontSize='12px' color='rgba(0,0,0,0.6)'>
                      • {perm}
                    </UTTypography>
                  ))}
                </UTBox>
              </UTBox>
            )}

            {/* Security Information */}
            <UTBox flexDirection='column' gap='12px'>
              <UTTypography fontSize='13px' fontWeight='600' color='rgba(0,0,0,0.7)'>
                Security Information
              </UTTypography>

              <UTBox
                flexDirection='row'
                justifyContent='space-between'
                py='8px'
                borderBottom='1px solid rgba(0,0,0,0.1)'
              >
                <UTTypography fontSize='13px' color='rgba(0,0,0,0.7)'>
                  Failed Login Attempts:
                </UTTypography>
                <UTBox
                  padding='2px 8px'
                  borderRadius='4px'
                  backgroundColor={
                    profile.failedLoginAttempts > 3
                      ? 'rgba(220, 38, 38, 0.1)'
                      : 'rgba(74, 144, 226, 0.1)'
                  }
                >
                  <UTTypography
                    fontSize='12px'
                    fontWeight='600'
                    color={
                      profile.failedLoginAttempts > 3
                        ? '#dc2626'
                        : ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND
                    }
                  >
                    {profile.failedLoginAttempts}
                  </UTTypography>
                </UTBox>
              </UTBox>

              <UTBox
                flexDirection='row'
                justifyContent='space-between'
                py='8px'
                borderBottom='1px solid rgba(0,0,0,0.1)'
              >
                <UTTypography fontSize='13px' color='rgba(0,0,0,0.7)'>
                  Account Status:
                </UTTypography>
                <UTBox
                  padding='2px 8px'
                  borderRadius='4px'
                  backgroundColor={isLocked ? 'rgba(220, 38, 38, 0.1)' : 'rgba(74, 144, 226, 0.1)'}
                >
                  <UTTypography
                    fontSize='12px'
                    fontWeight='600'
                    color={
                      isLocked ? '#dc2626' : ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND
                    }
                  >
                    {isLocked ? 'Locked' : 'Active'}
                  </UTTypography>
                </UTBox>
              </UTBox>

              {isLocked && profile.lockedUntil && (
                <UTBox
                  flexDirection='row'
                  justifyContent='space-between'
                  py='8px'
                  borderBottom='1px solid rgba(0,0,0,0.1)'
                >
                  <UTTypography fontSize='13px' color='rgba(0,0,0,0.7)'>
                    Locked Until:
                  </UTTypography>
                  <UTTypography fontSize='12px' fontWeight='600' color='#dc2626'>
                    {formatDate(profile.lockedUntil)}
                  </UTTypography>
                </UTBox>
              )}

              <UTBox flexDirection='row' justifyContent='space-between' py='8px'>
                <UTTypography fontSize='13px' color='rgba(0,0,0,0.7)'>
                  Last Login:
                </UTTypography>
                <UTTypography fontSize='13px' fontWeight='600'>
                  {profile.lastLoginAt ? formatDate(profile.lastLoginAt) : 'Never'}
                </UTTypography>
              </UTBox>
            </UTBox>
          </UTBox>
        ) : null
      }
    />
  );
};
